import { useId, useState } from 'react';
import { ExpandLess, ExpandMore, LocalFlorist } from '@mui/icons-material';
import { usePrefs } from '@/store/BuyerPrefsContext';
import allProduce from '@/assets/home-hero.png';
// 首页品类宫格：全部 + 各品类卡（首字圆形徽标），点击切换商品过滤
import { Box, Button, Typography } from '@mui/material';

export interface CategoryOption {
  id: string;
  name: string;
  imageUrl?: string | null;
}

interface CategoryGridProps {
  categories: CategoryOption[];
  activeId: string;
  allLabel: string;
  onSelect: (id: string) => void;
}

function CategoryImage({ src, label }: { src?: string | null; label: string }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  return src && failedSrc !== src ? <Box component="img" src={src} alt="" onError={() => setFailedSrc(src)} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : <LocalFlorist aria-label={label} sx={{ fontSize: 28 }} />;
}

export default function CategoryGrid({ categories, activeId, allLabel, onSelect }: CategoryGridProps) {
  const { t } = usePrefs();
  const [expanded, setExpanded] = useState(false);
  const gridId = useId();
  const firstRow = categories.slice(0, 7);
  const selected = categories.find(c => c.id === activeId);
  if (selected && !firstRow.some(c => c.id === selected.id)) firstRow.splice(6, 1, selected);
  const firstRowIds = new Set(firstRow.map(c => c.id));
  const cellSx = (active: boolean) => ({
    bgcolor: active ? '#ECFDF5' : '#FFFFFF',
    border: `1px solid ${active ? '#10B981' : '#F0F2F5'}`,
    borderRadius: '16px',
    p: { xs: 1, md: 1.5 },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0.75,
    cursor: 'pointer',
    transition: 'border-color .15s, background .15s',
    '&:hover': { borderColor: '#10B981' },
  });

  return (
    <Box>
    <Box id={gridId} sx={{
      display: { xs: 'flex', md: 'grid' },
      gridTemplateColumns: { md: 'repeat(8, 1fr)' },
      gap: 1,
      overflowX: { xs: 'auto', md: 'visible' },
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      '& > div': { flex: { xs: '0 0 calc((100% - 24px) / 4)', sm: '0 0 calc((100% - 40px) / 6)', md: 'initial' }, minWidth: 0 },
    }}>
      <Box onClick={() => onSelect('')} sx={cellSx(activeId === '')}>
        <Box
          sx={{
            width: { xs: 60, md: 64 }, height: { xs: 60, md: 64 }, borderRadius: '50%',
            bgcolor: activeId === '' ? '#10B981' : '#D1FAE5',
            color: activeId === '' ? '#fff' : '#065F46',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '15px', fontWeight: 700,
          }}
        >
          <Box component="img" src={allProduce} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        </Box>
        <Typography sx={{ fontSize: { xs: '13px', md: '12px' }, fontWeight: activeId === '' ? 600 : 400, color: '#212121', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {allLabel}
        </Typography>
      </Box>
      {categories.map((c) => {
        const active = activeId === c.id;
        return (
          <Box key={c.id} onClick={() => onSelect(c.id)} sx={{ ...cellSx(active), display: { xs: 'flex', md: expanded || firstRowIds.has(c.id) ? 'flex' : 'none' } }}>
            <Box
              sx={{
                width: { xs: 60, md: 64 }, height: { xs: 60, md: 64 }, borderRadius: '50%',
                bgcolor: active ? '#10B981' : '#D1FAE5',
                color: active ? '#fff' : '#065F46',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '15px', fontWeight: 700,
              }}
            >
              <CategoryImage src={c.imageUrl} label={c.name} />
            </Box>
            <Typography sx={{ fontSize: { xs: '13px', md: '12px' }, fontWeight: active ? 600 : 400, color: '#212121', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.name}
            </Typography>
          </Box>
        );
      })}
    </Box>
    {categories.length > 7 && (
      <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', mt: 1 }}>
        <Button size="small" aria-expanded={expanded} aria-controls={gridId}
          onClick={() => setExpanded(value => !value)} endIcon={expanded ? <ExpandLess /> : <ExpandMore />}>
          {expanded ? t('home.showFewerCategories') : t('home.showAllCategories', { count: categories.length })}
        </Button>
      </Box>
    )}
    </Box>
  );
}
